import React from 'react';
import { Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faEdit,
  faTrash,
  faClock,
  faTicketAlt,
  faCamera,
  faEllipsisV
} from '@fortawesome/free-solid-svg-icons';
import { getStatusBadge, getIncidentTimeInfo } from './tagStatusHelper';

const CardMenuToggle = React.forwardRef(({ onClick, isDark }, ref) => (
  <button
    ref={ref}
    type="button"
    className="btn btn-sm rounded-circle border-0 d-flex align-items-center justify-content-center shadow-none hover-opacity p-0"
    style={{
      width: '28px',
      height: '28px',
      color: isDark ? '#94a3b8' : '#64748b',
      backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
      cursor: 'pointer'
    }}
    onClick={e => {
      e.preventDefault();
      e.stopPropagation();
      onClick(e);
    }}
    title="Opciones"
  >
    <FontAwesomeIcon icon={faEllipsisV} style={{ fontSize: '0.85rem' }} />
  </button>
));

/**
 * Componente individual de tarjeta de incidencia en el catálogo
 * Con bordes redondeados modernos (rounded-4), alineación centrada y diseño pulido
 */
const TagCard = ({
  tag,
  isDark,
  onOpenView,
  onOpenEdit,
  onOpenComplete,
  onOpenDelete
}) => {
  const statusInfo = getStatusBadge(tag.iEstadoIncidencia, isDark);
  const timeInfo = getIncidentTimeInfo(tag);
  const isResolved = Number(tag.iEstadoIncidencia) === 3;



  return (
    <div className="col-12 col-md-6 col-lg-4">
      <div
        className="p-4 border h-100 d-flex flex-column justify-content-between transition-all position-relative"
        style={{
          background: isDark
            ? 'linear-gradient(145deg, #162438 0%, #101c2c 100%)'
            : '#ffffff',
          borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#e2e8f0',
          borderRadius: '20px',
          boxShadow: isDark
            ? '0 8px 24px -4px rgba(0,0,0,0.3)'
            : '0 8px 24px -4px rgba(0,0,0,0.04)',
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out'
        }}
        onClick={() => onOpenView(tag)}
      >
        <div>
          {/* Header de la tarjeta con Badges y Acciones */}
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center flex-wrap" style={{ gap: '6px' }}>
              {/* Badge #INC-125 */}
              <span
                className="badge px-2.5 py-1 rounded-pill fw-bold"
                style={{
                  backgroundColor: isDark ? '#1e2c3d' : '#e0e7ff',
                  color: isDark ? '#818cf8' : '#4338ca',
                  fontSize: '0.76rem',
                  letterSpacing: '0.2px'
                }}
              >
                #INC-{tag.idEtiqueta}
              </span>

              {/* Status Badge */}
              <span
                className="badge rounded-pill fw-semibold d-inline-flex align-items-center"
                style={{
                  backgroundColor: statusInfo.bg,
                  color: statusInfo.color,
                  border: `1px solid ${statusInfo.border}`,
                  fontSize: '0.72rem',
                  padding: '4px 10px',
                  gap: '6px'
                }}
              >
                <span
                  style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    backgroundColor: statusInfo.color,
                    marginRight: '6px',
                    flexShrink: 0
                  }}
                />
                <span>{statusInfo.label}</span>
              </span>
            </div>

            {/* Acciones Rápidas (3 puntitos) */}
            <div onClick={e => e.stopPropagation()}>
              <Dropdown align="end">
                <Dropdown.Toggle as={CardMenuToggle} isDark={isDark} id={`tag-card-menu-${tag.idEtiqueta}`} />

                <Dropdown.Menu
                  className="shadow-lg border py-1"
                  style={{
                    backgroundColor: isDark ? '#1a2636' : '#ffffff',
                    borderColor: isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0',
                    borderRadius: '12px',
                    minWidth: '150px',
                    boxShadow: isDark ? '0 12px 30px rgba(0,0,0,0.5)' : '0 10px 25px rgba(0,0,0,0.08)',
                    zIndex: 1060
                  }}
                >
                  <Dropdown.Item
                    as="button"
                    className="d-flex align-items-center py-2 px-3"
                    style={{
                      gap: '10px',
                      color: isDark ? '#f8fafc' : '#1e293b',
                      fontSize: '0.83rem'
                    }}
                    onClick={() => onOpenEdit(tag)}
                  >
                    <FontAwesomeIcon
                      icon={faEdit}
                      style={{ width: '14px', fontSize: '0.82rem', color: isDark ? '#94a3b8' : '#64748b' }}
                    />
                    <span className="fw-medium">Editar</span>
                  </Dropdown.Item>

                  {!isResolved && (
                    <Dropdown.Item
                      as="button"
                      className="d-flex align-items-center py-2 px-3"
                      style={{
                        gap: '10px',
                        color: isDark ? '#f8fafc' : '#1e293b',
                        fontSize: '0.83rem'
                      }}
                      onClick={() => onOpenComplete(tag)}
                    >
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        style={{ width: '14px', fontSize: '0.82rem', color: '#10b981' }}
                      />
                      <span className="fw-medium">Resolver</span>
                    </Dropdown.Item>
                  )}

                  <Dropdown.Divider style={{ borderColor: isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0', margin: '4px 0' }} />

                  <Dropdown.Item
                    as="button"
                    className="d-flex align-items-center py-2 px-3 text-danger"
                    style={{
                      gap: '10px',
                      color: '#ef4444',
                      fontSize: '0.83rem'
                    }}
                    onClick={() => onOpenDelete(tag)}
                  >
                    <FontAwesomeIcon
                      icon={faTrash}
                      style={{ width: '14px', fontSize: '0.82rem', color: '#ef4444' }}
                    />
                    <span className="fw-medium">Eliminar</span>
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>

          {/* Título de la incidencia */}
          <h6
            className="fw-bold mb-1.5"
            style={{
              color: isDark ? '#f8fafc' : '#0f172a',
              fontSize: '0.96rem',
              lineHeight: '1.4'
            }}
          >
            {tag.vNombre}
          </h6>

          {/* Tiempo de la incidencia */}
          <div className="mb-3">
            {timeInfo.isResolved ? (
              <span
                className="badge px-2.5 py-1 rounded-pill fw-semibold d-inline-flex align-items-center"
                style={{
                  backgroundColor: isDark ? 'rgba(16, 185, 129, 0.15)' : '#d1fae5',
                  color: isDark ? '#34d399' : '#047857',
                  border: `1px solid ${isDark ? 'rgba(16, 185, 129, 0.3)' : '#a7f3d0'}`,
                  fontSize: '0.74rem',
                  gap: '6px'
                }}
                title={`Tiempo de resolución: ${timeInfo.duration || timeInfo.label}`}
              >
                <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: '0.70rem' }} />
                <span>{timeInfo.duration ? `Resuelta en ${timeInfo.duration}` : timeInfo.label}</span>
              </span>
            ) : (
              <div
                className="small d-flex align-items-center"
                style={{
                  gap: '6px',
                  fontSize: '0.76rem',
                  color: isDark ? '#94a3b8' : '#64748b'
                }}
              >
                <FontAwesomeIcon icon={faClock} style={{ fontSize: '0.70rem', color: '#6366f1' }} />
                <span>{timeInfo.label}</span>
              </div>
            )}
          </div>

          {/* Descripción */}
          <div
            className="small mb-3"
            style={{
              fontSize: '0.83rem',
              color: isDark ? '#94a3b8' : '#64748b',
              lineHeight: '1.5',
              minHeight: '2.4em',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: '75px',
              overflowY: 'auto'
            }}
          >
            {tag.vDescripcion || <span className="fst-italic opacity-50">Sin descripción registrada</span>}
          </div>
        </div>

        <div>
          {/* Responsable */}
          <div
            className="p-2 d-flex align-items-center justify-content-between"
            style={{
              backgroundColor: isDark ? '#1a2939' : '#f8fafd',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#e2e8f0'}`,
              borderRadius: '12px',
              fontSize: '0.78rem',
              marginBottom: '10px'
            }}
          >
            <div className="d-flex align-items-center overflow-hidden">
              <span
                className="fw-semibold text-truncate"
                style={{ color: isDark ? '#e2e8f0' : '#1e293b' }}
              >
                {tag.vResponsable || 'Sin asignar'}
              </span>
            </div>
            <span className="text-muted" style={{ fontSize: '0.72rem' }}>
              Responsable
            </span>
          </div>

          {/* Contadores: Tickets vinculados y Fotos adjuntas */}
          <div className="d-flex align-items-center" style={{ gap: '8px' }}>
            <div
              className="d-flex align-items-center justify-content-between flex-grow-1"
              style={{
                backgroundColor: isDark ? '#1a2939' : '#f1f5f9',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#e2e8f0'}`,
                borderRadius: '50rem',
                padding: '6px 12px',
                fontSize: '0.76rem'
              }}
            >
              <div className="d-flex align-items-center" style={{ gap: '6px' }}>
                <FontAwesomeIcon icon={faTicketAlt} style={{ fontSize: '0.76rem', color: '#6366f1' }} />
                <span className="text-muted">Tickets:</span>
              </div>
              <span
                className="badge rounded-pill fw-bold"
                style={{
                  backgroundColor: (tag.iTotalTickets || 0) > 0 ? '#6366f1' : (isDark ? '#334155' : '#cbd5e1'),
                  color: '#ffffff',
                  fontSize: '0.72rem',
                  padding: '3px 8px',
                  lineHeight: 1
                }}
              >
                {tag.iTotalTickets || 0}
              </span>
            </div>

            <div
              className="d-flex align-items-center justify-content-between flex-grow-1"
              style={{
                backgroundColor: isDark ? '#1a2939' : '#f1f5f9',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#e2e8f0'}`,
                borderRadius: '50rem',
                padding: '6px 12px',
                fontSize: '0.76rem'
              }}
            >
              <div className="d-flex align-items-center" style={{ gap: '6px' }}>
                <FontAwesomeIcon icon={faCamera} style={{ fontSize: '0.76rem', color: '#0ea5e9' }} />
                <span className="text-muted">Archivos:</span>
              </div>
              <span
                className="badge rounded-pill fw-bold"
                style={{
                  backgroundColor: (tag.iTotalFotos || 0) > 0 ? '#0ea5e9' : (isDark ? '#334155' : '#cbd5e1'),
                  color: '#ffffff',
                  fontSize: '0.72rem',
                  padding: '3px 8px',
                  lineHeight: 1
                }}
              >
                {tag.iTotalFotos || 0}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TagCard;
